import * as R from 'ramda';
import { put, call, select, takeLatest } from 'redux-saga/effects';
// components
import { openLoader, closeLoader } from '../../components/loader/actions';
import {
  checkReportFunction,
  transformSearchCriteriaBeforeReportPost,
} from '../../components/edit-report/helpers';
// features
import { makeSelectCurrentBranchGuid } from '../branch/selectors';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// sagas
import { visitPageSaga as checkVisitPageSaga } from '../../sagas';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// centralized-dispatch
import * as A from './actions';
import * as H from './helpers';
//////////////////////////////////////////////////

const getCheckedReports = (data) => R.compose(
  checkReportFunction,
  G.getReportsSortedBySeqFreez,
)(data);

export function* getReportsSaga({ payload = {} } = {}) {
  try {
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    if (G.isNilOrEmpty(currentBranchGuid)) return H.getEmptyCentralizedDispatchReport();

    const params = {
      reportType: GC.CENTRALIZED_DISPATCH_REPORT,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
    };
    const res = yield call(sendRequest, 'get', endpointsMap.listReports, { params });
    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const reports = getCheckedReports(data);
      const report = R.or(
        G.findDefaultReport(reports),
        H.getEmptyCentralizedDispatchReport(),
      );

      yield put(A.setReports(reports));

      if (G.isTrue(R.propOr(false, 'setUsedReport', payload))) {
        yield put(A.setUsedReport(report));
      }

      return report;
    }

    yield call(G.handleFailResponse, res, 'getCentralizedDispatchReportsSaga fail');
  } catch (error) {
    yield call(G.handleException, error, 'getCentralizedDispatchReportsSaga exception');
  }

  return H.getEmptyCentralizedDispatchReport();
}

const prepareReportForRequest = (report) => R.assoc(
  'searchCriteria',
  transformSearchCriteriaBeforeReportPost(
    R.pathOr([], ['searchCriteria'], report),
    GC.ROUTE_TEL_REPORT,
  ),
  report,
);

const getSavedReport = (data) => R.head(checkReportFunction(R.of(Array, data)));

export function* createReportSaga({ payload }) {
  try {
    yield put(openLoader());

    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const report = prepareReportForRequest(payload);

    const options = {
      data: R.assoc(GC.FIELD_BRANCH_GUID, currentBranchGuid, report),
    };

    const res = yield call(sendRequest, 'post', endpointsMap.report, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setUsedReport(getSavedReport(data)));

      yield call(getReportsSaga);
    } else {
      yield call(G.handleFailResponse, res, 'createCentralizedDispatchReportSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'createCentralizedDispatchReportSaga exception');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  } finally {
    yield put(closeLoader());
  }
}

export function* updateReportSaga({ payload }) {
  try {
    yield put(openLoader());

    const report = prepareReportForRequest(payload);

    const res = yield call(sendRequest, 'put', endpointsMap.report, { data: report });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setUsedReport(getSavedReport(data)));

      yield call(getReportsSaga);
    } else {
      yield call(G.handleFailResponse, res, 'updateCentralizedDispatchReportSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'updateCentralizedDispatchReportSaga exception');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  } finally {
    yield put(closeLoader());
  }
}

export function* deleteReportSaga({ payload }) {
  try {
    yield put(openLoader());

    const guid = G.getGuidFromObject(payload);
    const endpoint = endpointsMap.getCurrentReportEndpoint(guid);

    const res = yield call(sendRequest, 'delete', endpoint);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(getReportsSaga, { payload: { setUsedReport: true } });
    } else {
      yield call(G.handleFailResponse, res, 'deleteCentralizedDispatchReportSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'deleteCentralizedDispatchReportSaga exception');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  } finally {
    yield put(closeLoader());
  }
}

export function* visitPageSaga({ payload }) {
  yield call(checkVisitPageSaga, payload, GC.CHECK_VISIT_CENTRALIZED_DISPATCH_PAGE);

  const report = yield call(getReportsSaga, { payload: { setUsedReport: true } });

  const filterValues = H.getFilterValues(report);
  const tripsOrderFields = H.getOrderFieldsForTripsFromFilters(filterValues);
  const tripsSearchCriteria = H.getSearchCriteriaForTripsFromFilters(filterValues);
  const driversRadiusRequestParams = H.getDriverRadiusRequestParams(filterValues);
  const driversSearchCriteria = H.getSearchCriteriaForDriversFromFilters(filterValues);

  yield put(A.setTripsOrderFields(tripsOrderFields));
  yield put(A.setTripsSearchCriteria(tripsSearchCriteria));
  yield put(A.setDriversSearchCriteria(driversSearchCriteria));
  yield put(A.setDriversRadiusRequestParams(driversRadiusRequestParams));

  yield put({
    type: GC.VISIT_DISPATCH_BOARD_TEL_PAGE,
    payload: {
      centralizedDispatch: true,
      searchCriteria: tripsSearchCriteria,
      route: GC.ROUTE_PATH_DISPATCH_BOARD_TEL,
    },
  });

  yield put({
    type: GC.VISIT_ROUTE_AVAILABLE_DRIVER_PAGE,
    payload: {
      centralizedDispatch: true,
      searchCriteria: driversSearchCriteria,
      pathname: GC.ROUTE_PATH_AVAILABLE_DRIVER_LIST,
    },
  });
}

export default function* centralizedDispatchWatcherSaga() {
  yield takeLatest(A.visitPage, visitPageSaga);
  yield takeLatest(A.getReportsRequest, getReportsSaga);
  yield takeLatest(A.createReportRequest, createReportSaga);
  yield takeLatest(A.deleteReportRequest, deleteReportSaga);
  yield takeLatest(A.updateReportRequest, updateReportSaga);
}
